import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Monitor, Edit, Trash2, Plus, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { ConfirmDialog } from '../components/ui/confirm-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminAds = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [ads, setAds] = useState([]);
  const [adDialog, setAdDialog] = useState(false);
  const [adForm, setAdForm] = useState({
    id: null,
    page: 'dashboard',
    position: 'row1_ad1',
    ad_type: 'html',
    content: '',
    is_active: true
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, adId: null });

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
      return;
    }
    fetchAds();
  }, [user, navigate, activeTab]);

  const fetchAds = async () => {
    setLoading(true);
    const token = localStorage.getItem('session_token');
    try {
      const response = await axios.get(`${API}/admin/ads?page=${activeTab}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAds(response.data);
    } catch (error) {
      console.error('Error fetching ads:', error);
      toast.error('Failed to load ads');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAd = async () => {
    if (!adForm.content.trim()) {
      toast.error('Ad content is required');
      return;
    }

    const token = localStorage.getItem('session_token');
    try {
      await axios.post(`${API}/admin/ads`, {
        page: adForm.page,
        position: adForm.position,
        ad_type: adForm.ad_type,
        content: adForm.content,
        is_active: adForm.is_active
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Ad saved successfully');
      setAdDialog(false);
      setAdForm({
        id: null,
        page: 'dashboard',
        position: 'row1_ad1',
        ad_type: 'html',
        content: '',
        is_active: true
      });
      fetchAds();
    } catch (error) {
      console.error('Save ad error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save ad');
    }
  };

  const handleDeleteAd = async (id) => {
    setDeleteDialog({ open: true, adId: id });
  };

  const confirmDeleteAd = async () => {
    const adId = deleteDialog.adId;
    setDeleteDialog({ open: false, adId: null });
    
    const token = localStorage.getItem('session_token');
    try {
      await axios.delete(`${API}/admin/ads/${adId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ad deleted successfully');
      fetchAds();
    } catch (error) {
      console.error('Delete ad error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete ad');
    }
  };

  const handleEditAd = (ad) => {
    setAdForm({
      id: ad.id,
      page: ad.page,
      position: ad.position,
      ad_type: ad.ad_type,
      content: ad.content,
      is_active: ad.is_active
    });
    setAdDialog(true);
  };

  const getPositionLabel = (position) => {
    const labels = {
      'row1_ad1': 'Row 1 - Ad 1 (Left)',
      'row1_ad2': 'Row 1 - Ad 2 (Right)',
      'row2_ad1': 'Row 2 - Ad 1 (Left)',
      'row2_ad2': 'Row 2 - Ad 2 (Right)'
    };
    return labels[position] || position;
  };

  const getAdTypeLabel = (type) => {
    const labels = {
      'html': 'HTML',
      'iframe': 'iFrame',
      'script': 'Script'
    };
    return labels[type] || type;
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center mb-8">
            <Button
              onClick={() => navigate('/admin')}
              variant="ghost"
              className="text-white hover:bg-white/10 mr-4"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-3xl sm:text-4xl font-bold text-white">Manage Ads</h1>
              <p className="text-gray-400">Configure advertisements for free users</p>
            </div>
          </div>

          {/* Info Banner */}
          <Card className="glass-dark border-white/10 mb-6">
            <CardContent className="p-4">
              <p className="text-sm text-gray-300">
                <strong className="text-white">Note:</strong> Ads are only shown to free users. Premium and Tipster users will not see any advertisements.
              </p>
              <p className="text-sm text-gray-400 mt-2">
                <strong>Ad Sizes:</strong> Dashboard ads: 728x90px (full width). Tips/Strategies ads: 468x60px (half width).
              </p>
              
              {/* Template Banner Code */}
              <div className="mt-4 pt-4 border-t border-white/10">
                <p className="text-sm font-semibold text-white mb-2">📢 Template: "Available Ad Spot" Banner</p>
                <p className="text-xs text-gray-400 mb-3">
                  Use this HTML code to display an "Advertise Here" banner that links to your advertising page:
                </p>
                
                {/* Dashboard Ad Template (728x90) */}
                <div className="mb-4">
                  <p className="text-xs font-semibold text-blue-400 mb-2">Dashboard Ad (728x90px):</p>
                  <div className="bg-slate-950/50 rounded p-3 border border-white/10">
                    <code className="text-xs text-green-400 block whitespace-pre-wrap break-all">
{`<a href="/advertising" target="_blank" style="display: block; width: 100%; cursor: pointer;">
  <img src="${BACKEND_URL}/api/uploads/ad_templates/ad_placeholder_728x90.png" 
       alt="Advertise Here" 
       style="width: 100%; height: auto; border-radius: 4px;" />
</a>`}
                    </code>
                  </div>
                </div>
                
                {/* Tips/Strategies Ad Template (468x60) */}
                <div>
                  <p className="text-xs font-semibold text-blue-400 mb-2">Tips/Strategies Ad (468x60px):</p>
                  <div className="bg-slate-950/50 rounded p-3 border border-white/10">
                    <code className="text-xs text-green-400 block whitespace-pre-wrap break-all">
{`<a href="/advertising" target="_blank" style="display: block; width: 100%; cursor: pointer;">
  <img src="${BACKEND_URL}/api/uploads/ad_templates/ad_placeholder_468x60.png" 
       alt="Advertise Here" 
       style="width: 100%; height: auto; border-radius: 4px;" />
</a>`}
                    </code>
                  </div>
                </div>
                
                <p className="text-xs text-gray-500 mt-3 italic">
                  💡 Tip: Copy the code above, paste it in the "Ad Content" field, and select "HTML" as the ad type.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto">
            <Button
              onClick={() => setActiveTab('dashboard')}
              variant={activeTab === 'dashboard' ? 'default' : 'outline'}
              className={activeTab === 'dashboard' ? 'bg-blue-600 hover:bg-blue-700' : 'border-white/20 text-white hover:bg-white/10'}
            >
              <Monitor className="w-4 h-4 mr-2" />
              Dashboard (2 ads)
            </Button>
            <Button
              onClick={() => setActiveTab('tips')}
              variant={activeTab === 'tips' ? 'default' : 'outline'}
              className={activeTab === 'tips' ? 'bg-blue-600 hover:bg-blue-700' : 'border-white/20 text-white hover:bg-white/10'}
            >
              <Monitor className="w-4 h-4 mr-2" />
              Tips (4 ads)
            </Button>
            <Button
              onClick={() => setActiveTab('strategies')}
              variant={activeTab === 'strategies' ? 'default' : 'outline'}
              className={activeTab === 'strategies' ? 'bg-blue-600 hover:bg-blue-700' : 'border-white/20 text-white hover:bg-white/10'}
            >
              <Monitor className="w-4 h-4 mr-2" />
              Strategies (4 ads)
            </Button>
          </div>

          {/* Ads List */}
          <Card className="glass-dark border-white/10">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-white capitalize">{activeTab} Ads</CardTitle>
                <Dialog open={adDialog} onOpenChange={setAdDialog}>
                  <DialogTrigger asChild>
                    <Button
                      onClick={() => setAdForm({
                        id: null,
                        page: activeTab,
                        position: 'row1_ad1',
                        ad_type: 'html',
                        content: '',
                        is_active: true
                      })}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Ad
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="glass-dark border-white/10 max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="text-white">{adForm.id ? 'Edit' : 'Add'} Ad</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-gray-300">Page</Label>
                        <Select value={adForm.page} onValueChange={(value) => setAdForm({ ...adForm, page: value })}>
                          <SelectTrigger className="bg-white/5 border-white/10 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="dashboard">Dashboard</SelectItem>
                            <SelectItem value="tips">Tips</SelectItem>
                            <SelectItem value="strategies">Strategies</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-gray-300">Position</Label>
                        <Select value={adForm.position} onValueChange={(value) => setAdForm({ ...adForm, position: value })}>
                          <SelectTrigger className="bg-white/5 border-white/10 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {adForm.page === 'dashboard' && (
                              <>
                                <SelectItem value="row1_ad1">Row 1 - Ad 1 (Left)</SelectItem>
                                <SelectItem value="row1_ad2">Row 1 - Ad 2 (Right)</SelectItem>
                              </>
                            )}
                            {(adForm.page === 'tips' || adForm.page === 'strategies') && (
                              <>
                                <SelectItem value="row1_ad1">Row 1 - Ad 1 (After Item 3)</SelectItem>
                                <SelectItem value="row1_ad2">Row 1 - Ad 2 (After Item 6)</SelectItem>
                                <SelectItem value="row2_ad1">Row 2 - Ad 1 (After Item 9)</SelectItem>
                                <SelectItem value="row2_ad2">Row 2 - Ad 2 (After Item 12)</SelectItem>
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-gray-300">Ad Type</Label>
                        <Select value={adForm.ad_type} onValueChange={(value) => setAdForm({ ...adForm, ad_type: value })}>
                          <SelectTrigger className="bg-white/5 border-white/10 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="html">HTML</SelectItem>
                            <SelectItem value="iframe">iFrame</SelectItem>
                            <SelectItem value="script">Script</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-gray-300">Ad Content</Label>
                        {adForm.ad_type === 'html' && (
                          <div className="text-xs text-gray-400 mb-2 bg-blue-900/20 border border-blue-500/30 rounded p-2">
                            <p className="font-semibold text-blue-300 mb-1">⚠️ Important: Use single quotes (') for URLs</p>
                            <p className="mb-1">Example: &lt;a href='http://example.com'&gt;&lt;img src='http://example.com/image.png' /&gt;&lt;/a&gt;</p>
                            <p className="text-yellow-300">❌ Don't use double quotes (") - they will cause URL issues</p>
                          </div>
                        )}
                        {adForm.ad_type === 'iframe' && (
                          <p className="text-xs text-gray-400 mb-1">
                            Enter iframe URL (e.g., https://example.com/ad)
                          </p>
                        )}
                        {adForm.ad_type === 'script' && (
                          <p className="text-xs text-gray-400 mb-1">
                            Enter complete script tag (e.g., &lt;script src='https://example.com/ad.js'&gt;&lt;/script&gt;)
                          </p>
                        )}
                        <Textarea
                          value={adForm.content}
                          onChange={(e) => setAdForm({ ...adForm, content: e.target.value })}
                          className="bg-white/5 border-white/10 text-white font-mono text-sm min-h-[200px]"
                          placeholder={adForm.ad_type === 'html' ? "<a href='https://example.com'><img src='https://example.com/banner.jpg' /></a>" : 
                                     adForm.ad_type === 'iframe' ? 'https://example.com/ad' :
                                     "<script src='https://example.com/ad.js'></script>"}
                        />
                      </div>

                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="is_active"
                          checked={adForm.is_active}
                          onChange={(e) => setAdForm({ ...adForm, is_active: e.target.checked })}
                          className="w-4 h-4"
                        />
                        <Label htmlFor="is_active" className="text-gray-300 cursor-pointer">
                          Active (show this ad to users)
                        </Label>
                      </div>

                      <Button onClick={handleSaveAd} className="w-full bg-blue-600 hover:bg-blue-700">
                        {adForm.id ? 'Update Ad' : 'Create Ad'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-gray-400 text-center py-8">Loading...</p>
              ) : (
                <div className="space-y-4">
                  {ads.length === 0 ? (
                    <p className="text-gray-400 text-center py-8">No ads configured for this page</p>
                  ) : (
                    ads.map((ad) => (
                      <Card key={ad.id} className="glass-dark border-white/10">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-white font-semibold">{getPositionLabel(ad.position)}</span>
                                <span className="text-xs bg-blue-600 text-white px-2 py-1 rounded">
                                  {getAdTypeLabel(ad.ad_type)}
                                </span>
                                {ad.is_active ? (
                                  <span className="flex items-center text-xs text-green-400">
                                    <Eye className="w-3 h-3 mr-1" />
                                    Active
                                  </span>
                                ) : (
                                  <span className="flex items-center text-xs text-gray-400">
                                    <EyeOff className="w-3 h-3 mr-1" />
                                    Inactive
                                  </span>
                                )}
                              </div>
                              <div className="bg-black/20 p-3 rounded mt-2 max-h-32 overflow-auto">
                                <code className="text-xs text-gray-300 break-all">
                                  {ad.content.length > 200 ? ad.content.substring(0, 200) + '...' : ad.content}
                                </code>
                              </div>
                            </div>
                            <div className="flex gap-2 ml-4">
                              <Button
                                onClick={() => handleEditAd(ad)}
                                variant="ghost"
                                size="sm"
                                className="text-blue-400 hover:text-blue-300"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                onClick={() => handleDeleteAd(ad.id)}
                                variant="ghost"
                                size="sm"
                                className="text-red-400 hover:text-red-300"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open, adId: null })}
        title="Delete Ad"
        description="Are you sure you want to delete this ad? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
        onConfirm={confirmDeleteAd}
      />
    </Layout>
  );
};

export default AdminAds;
